import type { Locale } from "../../domain/src/types.js";
/**
 * Every word the trainer says, in both languages it speaks.
 *
 * Hebrew instructions use impersonal language and role names include both
 * genders. Rule numbers, FIVB and net heights stay as they are in both languages.
 *
 * A test asserts the two tables hold exactly the same keys and that no Hebrew
 * string has English left in it, so a missed translation fails the build.
 */

export const LOCALES: Locale[] = ["en", "he"];

export const RTL_LOCALES = ["he"];

export const STRINGS = {
  en: {
    meta: {
      title: "Six — volleyball positions and rotations",
      description:
        "Pick a position and see where you stand in all six volleyball rotations, serving and receiving.",
      languageName: "English",
      switchTo: "עברית",
    },

    positions: {
      S: {
        label: "Setter",
        nickname: "runs the offence",
        blurb: "Takes the second ball of almost every rally and chooses which hitter gets it. Sets from the right of the net, so every rotation leads back to that spot.",
      },
      OH: {
        label: "Outside hitter",
        nickname: "left side",
        blurb: "Attacks from zone 4 and takes serve after serve. Two of you rotate opposite each other, so one is always at the net while the other is in the back row.",
      },
      OPP: {
        label: "Opposite",
        nickname: "right side",
        blurb: "Attacks from zone 2 and blocks their strongest hitter. Stays out of serve receive, leaving room to swing on every ball.",
      },
      MB: {
        label: "Middle blocker",
        nickname: "the middle",
        blurb: "Attacks quick from zone 3 and reads the setter to get a block up. When this role rotates to the back row, the libero replaces it.",
      },
      L: {
        label: "Libero",
        nickname: "the back row",
        blurb: "Digs and passes, and never leaves the back three zones. May not serve, block or attack above the net, and wears a different shirt so the referee can identify the libero.",
      },
      ALL: {
        label: "Not sure yet",
        nickname: "watch all six",
        blurb: "Follow the whole team rather than one player, and watch how six people move as one shape.",
      },
    },

    sceneLabels: {
      S: "S",
      OH1: "OH1",
      OH2: "OH2",
      MB1: "MB1",
      MB2: "MB2",
      OPP: "OPP",
      L: "L",
    },

    roles: {
      S: "the setter",
      OH1: "outside hitter 1",
      OH2: "outside hitter 2",
      MB1: "middle blocker 1",
      MB2: "middle blocker 2",
      OPP: "the opposite",
      L: "the libero",
    },

    zones: {
      1: "back right",
      2: "front right",
      3: "front centre",
      4: "front left",
      5: "back left",
      6: "back centre",
    },

    constraints: {
      nearerNetThan: "Stay nearer the net than {other} in zone {zone}.",
      behind: "Stay behind {other} in zone {zone}.",
      leftOf: "Stay left of {other} in zone {zone}.",
      rightOf: "Stay right of {other} in zone {zone}.",
    },

    badge: {
      ruleEnforcedTitle: "Rule-enforced",
      ruleEnforcedDetail:
        "As the serve is struck you must not have crossed the teammates beside you, or the one in front of or behind you. Cross one and the point is theirs.",
      tacticalTitle: "Tactical convention",
      tacticalDetail:
        "Your team is serving, so nobody can be caught out of position. You start here to cover the court, not because you have to.",
      freeTitle: "Rally is live",
      freeDetail:
        "The ball has been struck, so positions no longer bind anyone. Both teams may move anywhere on their own court.",
    },

    coach: {
      two: "two",
      three: "three",
      base: "Your rotational slot: the centre of your zone, before anyone shifts for tactics. The setter is in zone {setter}, leaving {hitters} front-row attackers.",
      receive:
        "Where you stand as the serve is struck. Three passers take the ball, the setter waits to release, the net players hold their zones.",
      receiveDeepCover:
        "Where you stand as the serve is struck. Three passers take the ball and the opposite covers deep behind them, for anything past the passing line.",
      receiveStack:
        "Outside hitter 2 passes on the left so she can attack from zone 4 sooner. Until contact she stays right of the middle in zone 3 and nearer the net than the opposite in zone 1.",
      serving:
        "Where you stand as your team serves. Nothing binds you here — the shape is about covering the court once the ball is gone.",
      shift:
        "The ball is struck and everyone is free. The setter goes right, the front-row outside goes left to hit, and the back row spreads into defence.",
      defence: "Against an opponent’s attack, a back-row setter stays in zone 1. She moves to the net to set only after the ball crosses the net and she sees it is not coming toward zone 1. If it is coming to her zone, she stays to defend the ball. A front-row setter blocks in zone 2. The back-row outside covers zone 6; the libero or middle covers zone 5.",
      liberoMayNotServe:
        "{middle} has rotated to zone 1 and must serve, and a libero never serves. So they come back on, and the libero waits off court.",
    },

    hud: {
      yourBase: "Your base",
      whoLimits: "Who limits you",
      coach: "Coach",
      zone: "Zone",
      zoneSub: "{zone} · rotation {n}",
      wholeTeam: "Whole team",
      allSix: "All six players, rotation {n}.",
      offCourt: "Off court",
      offCourtReplaced:
        "You are in the back row this rotation, so the libero comes in for you.",
      offCourtLibero:
        "The middle you replace has rotated to zone 1 and must serve, so you step off.",
      rotation: "Rotation {n}",
      setterInZone: "setter in zone {n}",
      moment: "Moment",
      play: "Play",
      details: "Details",
      close: "Close",
      changePosition: "Change position",
      settings: "Settings",
      previous: "Previous rotation",
      next: "Next rotation",
      legendYou: "you",
      legendSetter: "setter",
      pickRotation: "Jump to a rotation",
      jumpToZone: "Jump to the rotation where you stand in zone {n}",
    },

    phases: {
      base: "Base",
      serve: "Serve",
      switch: "Defence",
      defence: "Defence",
    },

    moments: {
      base: "Base",
      receive: "We receive",
      serve: "We serve",
      switch: "Defence",
      defence: "Defence",
      baseHint: "Your rotational slot, before anyone shifts",
      receiveHint: "They serve and we take it",
      serveHint: "Our serve",
      switchHint: "The ball is struck and everyone is free",
    },

    cameras: {
      broadcast: "Broadcast",
      topDown: "Top-down",
      behindServer: "Behind server",
      coach: "Coach",
      firstPerson: "Your eyes",
    },

    settings: {
      netHeight: "Net height",
      netWomen: "2.24 m",
      netMen: "2.43 m",
      court: "Court",
      zoneNumbers: "Zone numbers",
      showOpponents: "Show the other team",
      sound: "Sound",
      camera: "Camera",
      language: "Language",
    },

    title: {
      wordmarkStart: "SI",
      wordmarkEnd: "X",
      tagline:
        "Six players, six zones, six rotations. Pick your position and see exactly where you stand in every one of them — when your team serves, and when theirs does.",
      rules: "FIVB 2025–2028 rules",
      skip: "Skip the walkthrough",
      skipped: "Walkthrough off",
      choose: "Choose a position",
      withLibero: "Our team plays with a libero",
      withLiberoHint: "A back-row specialist who swaps in for a middle. Turn this off and both middles play the whole way round.",
    },

    guide: {
      stepOf: "Step {n} of {total}",
      next: "Next",
      explore: "Explore",
      skip: "Skip",
      s1title: "Start with your slot",
      s1body:
        "You are {role}. In rotation 1 your rotational slot is zone {zone} — {zoneName}. Every player has one, and everyone steps one place clockwise when the team wins the serve.",
      s2title: "Two neighbours limit you",
      s2body:
        "From above you can see who you are tied to. Only the players immediately beside and in front of or behind you constrain where you stand — never the diagonals. Cross one of them as the server strikes and it is a positional fault.",
      s3title: "Now the real shape",
      s3body:
        "This is the team as the serve is struck: three passers deep, the setter tucked in behind them, the net players holding their zones. Everyone is still legal — just arranged for the job.",
      s4title: "The moment you are free",
      s4body:
        "The ball has been hit, so the rules let go. The setter sprints to the right, the outsides take the left, the middle stays middle. Step the wheel to see all six rotations, and flip who serves to see what changes.",
    },
  },

  he: {
    meta: {
      title: "שש — תפקידים ורוטציות בכדורעף",
      description:
        "בחירת תפקיד מציגה את המיקום שלך בכל שש הרוטציות, בהגשת הקבוצה ובהגשת היריבה.",
      languageName: "עברית",
      switchTo: "English",
    },

    positions: {
      S: {
        label: "מוסרת",
        nickname: "ניהול ההתקפה",
        blurb: "התפקיד אחראי לכדור השני כמעט בכל ראלי ולבחירת יעד ההתקפה. המסירה יוצאת מצד ימין של הרשת, ולכן לשם מובילה התנועה מכל עמדה בסבב.",
      },
      OH: {
        label: "קבלנית",
        nickname: "צד שמאל",
        blurb: "התקפה מאזור 4 וקבלת הגשות. שני תפקידי הקבלה עומדים זה מול זה בסבב, כך שתמיד יש נציגות לתפקיד ברשת ובקו האחורי.",
      },
      OPP: {
        label: "מצליבה",
        nickname: "צד ימין",
        blurb: "התקפה מאזור 2 וחסימת ההתקפה החזקה של היריבה. התפקיד אינו משתתף בקבלת הגשות, וכך נשאר פנוי להתקפה בכל כדור.",
      },
      MB: {
        label: "קצרנית",
        nickname: "חסימת אמצע",
        blurb: "התקפות מהירות מאזור 3 וקריאת המסירה לצורך חסימה. במעבר לקו האחורי, הליברו מחליף את תפקיד האמצע.",
      },
      L: {
        label: "ליברו",
        nickname: "הקו האחורי",
        blurb: "תפקיד הגנה וקבלה בשלושת האזורים האחוריים. לליברו אסור להגיש, לחסום או לתקוף מעל הרשת. חולצה בצבע אחר מאפשרת לזהות את התפקיד.",
      },
      ALL: {
        label: "עוד אין תפקיד",
        nickname: "לראות את כל הקבוצה",
        blurb: "מעקב אחרי כל הקבוצה מציג איך ששת התפקידים זזים יחד בפריסה אחת.",
      },
    },

    sceneLabels: {
      S: "מוסרת",
      OH1: "קבלנית 1",
      OH2: "קבלנית 2",
      MB1: "קצרנית 1",
      MB2: "קצרנית 2",
      OPP: "מצליבה",
      L: "ליברו",
    },

    roles: {
      S: "המוסרת",
      OH1: "קבלנית 1",
      OH2: "קבלנית 2",
      MB1: "קצרנית 1",
      MB2: "קצרנית 2",
      OPP: "המצליבה",
      L: "הליברו",
    },

    zones: {
      1: "אחורי ימין",
      2: "קדמי ימין",
      3: "קדמי מרכז",
      4: "קדמי שמאל",
      5: "אחורי שמאל",
      6: "אחורי מרכז",
    },

    constraints: {
      nearerNetThan: "יש להישאר קרוב יותר לרשת מאשר {other} באזור {zone}.",
      behind: "יש להישאר מאחורי {other} באזור {zone}.",
      leftOf: "יש להישאר מצד שמאל של {other} באזור {zone}.",
      rightOf: "יש להישאר מצד ימין של {other} באזור {zone}.",
    },

    badge: {
      ruleEnforcedTitle: "החוק מחייב",
      ruleEnforcedDetail:
        "ברגע ההגשה אסור לחצות את העמדות הסמוכות — לצדך, לפניך או מאחוריך. חצייה כזו מעניקה את הנקודה ליריבה.",
      tacticalTitle: "מוסכמה טקטית",
      tacticalDetail:
        "הקבוצה שלך מגישה, ולכן אין הגבלת סדר עמדות. המיקום כאן נועד לכיסוי המגרש.",
      freeTitle: "הראלי חי",
      freeDetail:
        "הכדור נחבט, והמיקומים כבר לא מחייבים. שתי הקבוצות חופשיות לזוז לכל מקום במגרש שלהן.",
    },

    coach: {
      two: "שני",
      three: "שלושה",
      base: "העמדה שלך ברוטציה: מרכז האזור שלך, לפני תנועה משיקולים טקטיים. עמדת המסירה באזור {setter}, ולכן יש {hitters} תפקידי התקפה בקו הקדמי.",
      receive:
        "זה המיקום שלך ברגע ההגשה. שלושת תפקידי הקבלה מקבלים את הכדור, עמדת המסירה ממתינה ליציאה לרשת, והקו הקדמי שומר על סדר האזורים.",
      receiveDeepCover:
        "זה המיקום שלך ברגע ההגשה. שלושת תפקידי הקבלה מקבלים את הכדור, ותפקיד ההצלבה מכסה עמוק מאחוריהם — כל כדור שעובר את קו הקבלה.",
      receiveStack:
        "קבלנית 2 מקבלת משמאל כדי להגיע מהר יותר להתקפה מאזור 4. עד רגע ההגשה היא נשארת מימין לקצרנית באזור 3 וקרוב יותר לרשת מהמצליבה באזור 1.",
      serving:
        "זה המיקום שלך כשהקבוצה שלך מגישה. אין כאן הגבלת סדר עמדות — הפריסה נועדה לכסות את המגרש ברגע שהכדור עובר.",
      shift:
        "הכדור נחבט והתנועה חופשית. תפקיד המסירה נע לימין, תפקיד הקבלה הקדמי לשמאל להתקפה, והקו האחורי נפרס להגנה.",
      defence: "מול התקפת היריבה, מוסרת בקו האחורי נשארת להגנה באזור 1. רק אחרי שהכדור חוצה את הרשת והיא מזהה שהוא לא לכיוון אזור 1, היא יוצאת לרשת למסירה. אם הכדור מגיע לאזור שלה, היא נשארת להגן עליו. מוסרת בקו הקדמי חוסמת באזור 2. הקבלנית האחורית מגנה באזור 6, והליברו או הקצרנית באזור 5.",
      liberoMayNotServe:
        "{middle} באזור 1, שבו חלה חובת הגשה. לליברו אסור להגיש, ולכן תפקיד האמצע חוזר למגרש והליברו מחוץ למגרש.",
    },

    hud: {
      yourBase: "העמדה שלך",
      whoLimits: "מה מגביל את המיקום שלך",
      coach: "הדרכה",
      zone: "אזור",
      zoneSub: "{zone} · רוטציה {n}",
      wholeTeam: "כל הקבוצה",
      allSix: "כל ששת התפקידים, רוטציה {n}.",
      offCourt: "מחוץ למגרש",
      offCourtReplaced:
        "התפקיד שלך בקו האחורי ברוטציה הזו, ולכן הליברו נכנס במקומו.",
      offCourtLibero:
        "תפקיד האמצע באזור 1 וחייב בהגשה, ולכן המקום שלך כליברו מחוץ למגרש.",
      rotation: "רוטציה {n}",
      setterInZone: "עמדת המסירה באזור {n}",
      moment: "רגע במשחק",
      play: "הרצה",
      details: "פרטים",
      close: "סגירה",
      changePosition: "החלפת תפקיד",
      settings: "הגדרות",
      previous: "רוטציה קודמת",
      next: "רוטציה הבאה",
      legendYou: "המיקום שלך",
      legendSetter: "מוסרת",
      pickRotation: "מעבר לרוטציה",
      jumpToZone: "מעבר לרוטציה שבה המקום שלך באזור {n}",
    },

    phases: {
      base: "בסיס",
      serve: "הגשה",
      switch: "הגנה",
      defence: "הגנה",
    },

    moments: {
      base: "בסיס",
      receive: "קבלה",
      serve: "סרב",
      switch: "הגנה",
      defence: "הגנה",
      baseHint: "העמדה שלך בסבב, לפני תחילת התנועה",
      receiveHint: "היריבה מגישה והקבוצה שלנו מקבלת",
      serveHint: "ההגשה שלנו",
      switchHint: "הכדור נחבט והתנועה חופשית",
    },

    cameras: {
      broadcast: "שידור",
      topDown: "מבט על",
      behindServer: "מאחורי ההגשה",
      coach: "הדרכה",
      firstPerson: "העיניים שלך",
    },

    settings: {
      netHeight: "גובה הרשת",
      netWomen: "2.24 מ׳",
      netMen: "2.43 מ׳",
      court: "מגרש",
      zoneNumbers: "מספרי אזורים",
      showOpponents: "הצגת הקבוצה היריבה",
      sound: "צליל",
      camera: "מצלמה",
      language: "שפה",
    },

    title: {
      wordmarkStart: "ש",
      wordmarkEnd: "ש",
      tagline:
        "שישה על המגרש, שישה אזורים, שש רוטציות. בחירת תפקיד מציגה את המיקום שלך בכל רוטציה — בהגשת הקבוצה ובהגשת היריבה.",
      rules: "חוקת FIVB 2025–2028",
      skip: "דילוג על ההדרכה",
      skipped: "ההדרכה כבויה",
      choose: "בחירת תפקיד",
      withLibero: "לקבוצה שלנו יש ליברו",
      withLiberoHint: "תפקיד הגנה שמחליף את תפקיד האמצע בקו האחורי. בלי ליברו, שני תפקידי האמצע משחקים לאורך כל הסבב.",
    },

    guide: {
      stepOf: "שלב {n} מתוך {total}",
      next: "הבא",
      explore: "לחקור",
      skip: "דילוג",
      s1title: "התחלה מהעמדה שלך",
      s1body:
        "התפקיד שלך: {role}. ברוטציה 1 העמדה שלך היא אזור {zone} — {zoneName}. לכל תפקיד יש עמדה כזו, וכל הקבוצה זזה מקום אחד עם כיוון השעון עם הזכייה בהגשה.",
      s2title: "העמדות הסמוכות מגבילות את המיקום שלך",
      s2body:
        "ממבט על אפשר לראות אילו עמדות מגבילות את המיקום שלך. רק העמדות הצמודות — לצדך, לפניך או מאחוריך — קובעות את סדר העמידה, ולא העמדות האלכסוניות. חצייה של עמדה סמוכה ברגע ההגשה היא עבירת מיקום.",
      s3title: "עכשיו הפריסה האמיתית",
      s3body:
        "כך נראית הקבוצה ברגע ההגשה: שלושת תפקידי הקבלה בעומק, עמדת המסירה מאחוריהם, והקו הקדמי שומר על סדר האזורים. הפריסה חוקית ומותאמת למשימה.",
      s4title: "הרגע שבו התנועה חופשית",
      s4body:
        "הכדור נחבט, ואפשר לנוע בחופשיות. תפקיד המסירה נע לימין, תפקידי הקבלה לשמאל, ותפקיד האמצע נשאר במרכז. אפשר לבחור בגלגל כל אחת משש הרוטציות ולהחליף את הקבוצה המגישה כדי לראות מה משתנה.",
    },
  },
};

/** Look up a dotted key and fill in {placeholders}. */
export function t(locale: string, path: string, params: Record<string, unknown> = {}) {
  const table = STRINGS[locale as keyof typeof STRINGS] ?? STRINGS.en;
  const value: unknown = path.split(".").reduce<unknown>((node, key) => node != null && typeof node === "object" ? (node as Record<string, unknown>)[key] : undefined, table);

  if (typeof value !== "string") {
    throw new Error(`missing string: ${path} (${locale})`);
  }

  let result = value;
  for (const [key, replacement] of Object.entries(params)) {
    result = result.replaceAll(`{${key}}`, String(replacement));
  }
  return result;
}

export const isRtl = (locale: string) => RTL_LOCALES.includes(locale);

/**
 * Hebrew glues single-letter prepositions onto the next word, and the definite
 * "ה" is swallowed when it does: ל + המוסרת is למוסרת, never להמוסרת.
 */
export const withoutArticle = (label: string) =>
  label.startsWith("ה") ? label.slice(1) : label;
